import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { scrollToSection } from '../utils/scroll';
import { trackCTA } from '../services/analytics';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navItems = [
    { name: 'Home', targetId: 'hero' },
    { name: 'Services', targetId: 'solutions' },
    { name: 'Work', targetId: 'work' },
    { name: 'Process', targetId: 'process' },
    { name: 'FAQ', targetId: 'faq' },
    { name: 'Contact', targetId: 'contact' },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 w-full z-50 px-4 sm:px-6 md:px-12 h-16 sm:h-20 flex items-center justify-between transition-all duration-500 ease-in-out border-b ${isScrolled
          ? 'bg-[#050505]/70 backdrop-blur-xl border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
          : 'bg-transparent border-transparent'
          }`}
      >
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="flex items-center h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md transition-transform hover:scale-[1.02] shrink-0"
          aria-label="Orbitra Solution Home"
        >
          <img
            src="/logo-text.png"
            alt="Orbitra Solution — Digital Studio &amp; AI Automation"
            title="Orbitra Solution"
            width="160"
            height="44"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="h-9 sm:h-11 md:h-13 w-auto object-contain"
            style={{ filter: "invert(1) grayscale(1) brightness(3)", mixBlendMode: "screen" }}
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" aria-label="Desktop Navigation">
          {navItems.map((item) => (
            <button
              key={item.name}
              type="button"
              onClick={() => { scrollToSection(item.targetId); trackCTA(`nav_${item.name.toLowerCase()}`, item.targetId); }}
              className="text-sm font-medium text-gray-300 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm px-1 py-0.5 cursor-pointer"
            >
              {item.name}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4 h-full">
          <button
            type="button"
            onClick={() => { scrollToSection('contact'); trackCTA('nav_start_project', 'contact'); }}
            className="interactive hidden sm:inline-flex px-6 py-2.5 rounded-full bg-white text-black text-sm font-medium hover:scale-105 transition-transform min-h-[44px] items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white cursor-pointer"
          >
            Start a Project
          </button>

          {/* Mobile Menu Toggle Button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden interactive flex items-center justify-center w-12 h-12 rounded-full text-white bg-transparent active:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white shrink-0 -mr-2 cursor-pointer"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            aria-label={mobileMenuOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.header>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            id="mobile-navigation"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#050505]/95 backdrop-blur-xl flex flex-col justify-between p-8 pt-[calc(6rem+env(safe-area-inset-top))] pb-[calc(2rem+env(safe-area-inset-bottom))] px-[calc(2rem+env(safe-area-inset-left))] pr-[calc(2rem+env(safe-area-inset-right))] md:hidden"
          >

            <nav className="flex flex-col gap-6" aria-label="Mobile Navigation">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    scrollToSection(item.targetId);
                    trackCTA(`mobile_nav_${item.name.toLowerCase()}`, item.targetId);
                  }}
                  className="text-3xl font-serif text-white hover:text-gray-300 transition-colors border-b border-white/10 pb-4 min-h-[48px] flex items-center text-left w-full cursor-pointer"
                >
                  {item.name}
                </button>
              ))}
            </nav>

            <div className="flex flex-col gap-4 mt-8">
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToSection('contact');
                  trackCTA('mobile_nav_start_project', 'contact');
                }}
                className="w-full py-4 rounded-full bg-white text-black text-center font-medium text-base hover:bg-gray-200 transition-colors min-h-[48px] flex items-center justify-center cursor-pointer"
              >
                Start a Project
              </button>
              <div className="text-xs text-gray-500 text-center font-light pt-4">
                © {new Date().getFullYear()} Orbitra Solution
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
